import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

interface EventPaginationProps {
    currentPage: number;
    totalPages: number;
    totalEvents: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (itemsPerPage: number) => void;
}

export const EventPagination: React.FC<EventPaginationProps> = ({ currentPage, totalPages, totalEvents, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
    if (totalPages === 0) return null;

    return (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalEvents)}</span> of <span className="font-medium">{totalEvents}</span> results
                    </div>

                    <div className="flex items-center gap-2">
                        <label htmlFor="itemsPerPage" className="text-sm text-gray-700 whitespace-nowrap">
                            Rows per page:
                        </label>
                        <select id="itemsPerPage" value={itemsPerPage} onChange={(e) => onItemsPerPageChange(Number(e.target.value))} className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent bg-white">
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="flex items-center gap-1">
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                    </Button>

                    <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                            // Show first page, last page, current page, and pages around current
                            const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);

                            if (!showPage) {
                                // Show ellipsis for skipped pages
                                if (page === currentPage - 2 || page === currentPage + 2) {
                                    return (
                                        <span key={page} className="px-2 text-gray-500">
                                            ...
                                        </span>
                                    );
                                }
                                return null;
                            }

                            return (
                                <Button key={page} variant={currentPage === page ? 'default' : 'outline'} size="sm" onClick={() => onPageChange(page)} className={`min-w-[40px] ${currentPage === page ? 'bg-brand text-white hover:bg-brand-hover' : ''}`}>
                                    {page}
                                </Button>
                            );
                        })}
                    </div>

                    <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="flex items-center gap-1">
                        Next
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};
